import {
  AddIcon,
  Button,
  Card,
  Grid,
  GridColumn,
  GridRow,
  ImportIcon,
  LazyLoader,
  Modal,
  SearchInput,
  Skeleton,
  TabBar,
  Tooltip,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { ReactNode, Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ExclusionTimeframeForm } from '../../../../../components/ExclusionTimeframe/ExclusionTimeframeForm';
import { useTeachingBlockStore } from '../../../../../components/TeachingBlockGrid/Contexts/TeachingBlockContext';
import { Exclusions } from '../../../../../components/TeachingBlockVersion/Exclusions/Exclusions';
import { TeachingBlockVersionTableType } from '../../../../../components/TeachingBlockVersion/graphql/types';
import { useTeachingBlockVersionData } from '../../../../../components/TeachingBlockVersion/hooks/useTeachingBlockVersionData';
import { useAuthClaims } from '../../../../../hooks/useAuthClaims';
import { useLoadBasicData } from '../../../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../../../hooks/useMemorizedCacheTag';
import {
  TeachingBlockResourceType,
  useConnectEntityWithVersionMutation,
  useRemoveTeachingBlockVersionResourcesMutation,
} from '../../../../../types/planung-graphql-client-defs';
import { createLessonFilterUrl } from '../../../../../utils/create-lesson-filter-url';
import { showSuccessSaveToast, showUserErrorToast } from '../../../../../utils/toast';
import styles from './TeachingBlockVersionData.module.scss';

const RESOURCE_TYPE = {
  teacher: TeachingBlockResourceType.Teacher,
  subject: TeachingBlockResourceType.Subject,
  room: TeachingBlockResourceType.Room,
} as const;

type TeachingBlockStore = ReturnType<typeof useTeachingBlockStore>;
type BasicData = ReturnType<typeof useLoadBasicData>;

/**
 * Shared shell for the per-resource teaching-block data pages (Teachers/Subjects/Rooms). These three
 * pages were near-identical copies; the only per-resource variation is captured in the config below
 * (entity type, store/data accessors, row mapping, the resource's table component). The Classes page
 * is intentionally NOT built on this shell — it carries unique remove/confirm/usage-tracking logic.
 */
export type TeachingBlockResourceConfig = {
  entityType: 'teacher' | 'subject' | 'room';
  exclusionHolderType: 'Person' | 'Subject' | 'Room';
  /** Relation field on the TeachingBlockVersion to disconnect from when removing an entity. */
  relationKey: 'teachers' | 'subjects' | 'rooms';
  lessonFilterColumn: Parameters<typeof createLessonFilterUrl>[0]['column'];
  searchName: string;
  importLabelKey: string;
  getVersionUuids: (store: TeachingBlockStore) => string[];
  getRows: (basicData: BasicData, versionUuids: string[]) => TeachingBlockVersionTableType[];
  /** Removes the entities from the in-memory store after the version was updated (instant feedback). */
  removeEntities: (store: TeachingBlockStore, uuids: string[]) => void;
  renderTable: (args: {
    versionUuid?: string;
    search: string;
    versionUuids: string[];
    onRowSelection: (selected: TeachingBlockVersionTableType) => void;
    onShowLessonClick: (uuid: string) => void;
    onRemove: (uuids: string[]) => Promise<void>;
  }) => ReactNode;
  /** Defaults to always-disabled (the import action is a placeholder for most resources). */
  importDisabled?: (store: TeachingBlockStore, basicData: BasicData) => boolean;
};

export const TeachingBlockResourceDataPage = observer(({ config }: { config: TeachingBlockResourceConfig }) => {
  const { t } = useTranslation();
  const { teachingBlockVersionUuid } = useParams();
  const { pimAuthClaims } = useAuthClaims();
  const navigate = useNavigate();

  const [tab, setTab] = useState<'exclusion' | 'condition'>('exclusion');
  const [exclusionModalOpen, setExclusionModalOpen] = useState<boolean>(false);
  const [selectedExclusionUuid, setSelectedExclusionUuid] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');

  const context = useMemorizedCacheTag('TEACHINGBLOCK_VERSIONS');
  const availabilityContext = useMemorizedCacheTag('TEACHING_BLOCK_AVAILABILITY_EXCLUSION');

  const { versionData } = useTeachingBlockVersionData(teachingBlockVersionUuid);

  const [, updateVersion] = useConnectEntityWithVersionMutation();
  const [, removeVersionResources] = useRemoveTeachingBlockVersionResourcesMutation();

  const basicData = useLoadBasicData({ pause: !teachingBlockVersionUuid });

  const store = useTeachingBlockStore();
  const versionUuids = config.getVersionUuids(store);

  const rows = useMemo(() => config.getRows(basicData, versionUuids), [basicData, versionUuids, config]);

  const [selected, setSelected] = useState<TeachingBlockVersionTableType | null>(rows[0] ?? null);

  async function handleImport() {
    if (teachingBlockVersionUuid) {
      const result = await updateVersion(
        {
          organizationUuid: pimAuthClaims.getOrganization().uuid,
          versionUuid: teachingBlockVersionUuid,
          entityType: config.entityType,
        },
        context,
      );
      if (!result || result.error) {
        showUserErrorToast({ text: t('common.errorToastText'), error: result.error });
      } else {
        // Die Mutation invalidiert die TeachingBlockVersion-Query; der Sync-Effekt im
        // TeachingBlockContext übernimmt die neu verbundenen Ressourcen in den Store,
        // sodass die Tabelle ohne Reload aktualisiert.
        showSuccessSaveToast();
      }
    }
  }

  const selectedExclusionUuids = versionData?.exclusions
    .filter((e) => e.holderUuid === selected?.uuid)
    .map((e) => e.uuid);

  const onExclusionEdit = (uuid: string) => {
    setSelectedExclusionUuid(uuid);
    setExclusionModalOpen(true);
  };

  const onExclusionModalClose = () => {
    setSelectedExclusionUuid(null);
    setExclusionModalOpen(false);
  };

  const handleRemove = async (uuids: string[]) => {
    if (!teachingBlockVersionUuid || uuids.length === 0) {
      return;
    }

    // Das Backend löst die Ressourcen aus der Version und löscht ihre Ausschlüsse in dieser Version mit.
    const result = await removeVersionResources(
      { versionUuid: teachingBlockVersionUuid, resourceType: RESOURCE_TYPE[config.entityType], uuids },
      { additionalTypenames: [...context.additionalTypenames, ...availabilityContext.additionalTypenames] },
    );

    if (!result || result.error) {
      showUserErrorToast({ text: t('common.errorToastText'), error: result?.error });
      return;
    }

    config.removeEntities(store, uuids);
    if (selected && uuids.includes(selected.uuid)) {
      setSelected(null);
    }
    showSuccessSaveToast();
  };

  const onShowLessonClick = (uuid: string) => {
    if (teachingBlockVersionUuid) {
      navigate(
        createLessonFilterUrl({
          context: 'epochenplan',
          versionUuid: teachingBlockVersionUuid,
          column: config.lessonFilterColumn,
          filterValue: uuid,
        }),
      );
    }
  };

  return (
    <Grid>
      <GridRow spacingBottom='none'>
        <SearchInput
          name={config.searchName}
          placeholder={t('common.search')}
          onChange={(e) => setSearch(e.target.value)}
          value={search}
        />
        <Button
          hierarchy='tertiary'
          icon={<ImportIcon />}
          style={{ marginLeft: 'auto' }}
          onClick={handleImport}
          disabled={config.importDisabled ? config.importDisabled(store, basicData) : true}
        >
          {t('teachingBlockVersion.import', { import: t(config.importLabelKey, { count: 2 }) })}
        </Button>
      </GridRow>
      <GridRow spacingTop='m'>
        <GridColumn width={rows.length === 0 ? 12 : 6}>
          {config.renderTable({
            versionUuid: teachingBlockVersionUuid,
            search,
            versionUuids,
            onRowSelection: (s) => setSelected(s),
            onShowLessonClick,
            onRemove: handleRemove,
          })}
        </GridColumn>
        {rows.length !== 0 && (
          <GridColumn width={6}>
            <Suspense fallback={<LazyLoader />}>
              <Card className={styles['teaching-block-version-data']} contentPadding='none'>
                <small>{t('teachingBlockVersion.specificInfo', { for: selected?.name })}</small>
                <div className={styles['mini-tab']}>
                  <TabBar
                    backgroundColor={'none'}
                    className={styles.tabs}
                    onValueChange={(content) => setTab(content as 'exclusion' | 'condition')}
                    tabs={[
                      {
                        trigger: t('exclusion.title', { count: 2 }),
                        id: 'exclusion',
                        content: (
                          <Exclusions
                            versionUuid={teachingBlockVersionUuid ?? ''}
                            uuids={selectedExclusionUuids ?? []}
                            onEdit={onExclusionEdit}
                            entityType={config.entityType}
                          />
                        ),
                      },
                    ]}
                  />
                  <div className={styles['additional-actions']}>
                    {tab === 'exclusion' && (
                      <Tooltip content={t('exclusion.add')}>
                        <Button
                          disabled={store.readonly}
                          hierarchy='ghost'
                          icon={<AddIcon />}
                          onClick={() => setExclusionModalOpen(true)}
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
              </Card>
            </Suspense>
          </GridColumn>
        )}
      </GridRow>
      <Modal
        width='s'
        title={t('exclusion.add')}
        isOpen={exclusionModalOpen}
        onRequestClose={() => onExclusionModalClose()}
      >
        <ExclusionTimeframeForm
          type={config.exclusionHolderType}
          holderUuid={selected?.uuid ?? ''}
          teachingBlockVersionUuid={teachingBlockVersionUuid ?? ''}
          exclusionUuid={selectedExclusionUuid}
          onClose={() => onExclusionModalClose()}
        />
      </Modal>
    </Grid>
  );
});
